import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Button,
  ConfigProvider,
  DatePicker,
  Form,
  InputNumber,
  Select,
  Skeleton,
  Space,
} from 'antd'
import { SaveOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import ruRU from 'antd/es/locale/ru_RU'
import B2BLayout from '../../../../layouts/b2b'
import fetchAPI from '../../../../../lib/utils/fetch-api'
import { objectToUrl } from '../../../utils'
import { MapContent, MapTitle, MapWrapper, AlertWrapper, TaskWrapper } from './styles'
import './index.css'

const breadcrumbs = [
  {
    href: '/admin',
    title: 'Админка',
  },
  {
    href: '/hr/payments',
    title: 'Список оплат',
  },
]

interface PaymentFormValues {
  period?: [Dayjs, Dayjs]
  payment_month?: Dayjs
  actual_payment_date?: Dayjs | null
  resource_id: number
  payment_plan: number
  payment_fact: number
}

const formatMoney = (value: number) =>
  new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: 2,
  }).format(value)

const getMonthlyAmountText = (value: number | undefined, monthsCount: number) => {
  const totalCents = Math.round(Number(value || 0) * 100)
  const baseCents = Math.floor(totalCents / monthsCount)
  const lastMonthCents = baseCents + totalCents - baseCents * monthsCount

  if (baseCents === lastMonthCents) {
    return `по ${formatMoney(baseCents / 100)} каждый месяц`
  }

  return `по ${formatMoney(baseCents / 100)}, в последнем месяце — ${formatMoney(
    lastMonthCents / 100,
  )}`
}

const getMonthsLabel = (count: number) => {
  const lastTwoDigits = count % 100
  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) return `${count} месяцев`
  if (count % 10 === 1) return `${count} месяц`
  if (count % 10 >= 2 && count % 10 <= 4) return `${count} месяца`
  return `${count} месяцев`
}

const getErrorMessages = (result: Record<string, any>): string[] => {
  if (Array.isArray(result?.errors)) {
    return result.errors.map(error => error?.msg || error?.detail || String(error))
  }
  if (result?.error) {
    return [typeof result.error === 'string' ? result.error : result.error.message]
  }

  return []
}

export default function HrPaymentCard() {
  const { id = 'new' } = useParams()
  const navigate = useNavigate()
  const [form] = Form.useForm<PaymentFormValues>()
  const [isLoading, setIsLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [pageErrors, setPageErrors] = useState<string[]>([])
  const [resources, setResources] = useState<Array<{ label: string; value: number }>>([])

  const period = Form.useWatch('period', form)
  const paymentPlan = Form.useWatch('payment_plan', form)
  const paymentFact = Form.useWatch('payment_fact', form)

  const monthsCount = useMemo(() => {
    if (!period?.[0] || !period?.[1]) return 0

    return period[1].startOf('month').diff(period[0].startOf('month'), 'month') + 1
  }, [period])

  const loadData = useCallback(async () => {
    setIsLoading(true)
    setLoadFailed(false)
    setPageErrors([])

    try {
      const enumsList = { types: ['hr_resource'] }
      const requests: Promise<Record<string, any>>[] = [
        fetchAPI(`/api/enum-list${objectToUrl(enumsList)}`),
      ]
      if (id !== 'new') {
        requests.push(fetchAPI(`/api/hr/payments/${id}`))
      }

      const [enums, payment] = await Promise.all(requests)
      setResources(
        (enums?.hr_resource || []).map(resource => ({
          label: resource.value,
          value: resource.id,
        })),
      )

      if (payment) {
        const paymentData = Array.isArray(payment)
          ? payment.reduce((result, field) => ({ ...result, [field.name]: field.value }), {})
          : payment
        form.setFieldsValue({
          payment_month: dayjs(paymentData.payment_date),
          actual_payment_date: paymentData.actual_payment_date
            ? dayjs(paymentData.actual_payment_date)
            : null,
          resource_id: paymentData.resource_id,
          payment_plan: Number(paymentData.payment_plan),
          payment_fact: Number(paymentData.payment_fact),
        })
      } else {
        const currentMonth = dayjs().startOf('month')
        form.setFieldsValue({
          period: [currentMonth, currentMonth],
          payment_plan: 0,
          payment_fact: 0,
        } as PaymentFormValues)
      }
    } catch (error) {
      setLoadFailed(true)
      setPageErrors([error instanceof Error ? error.message : 'Не удалось загрузить оплату'])
    } finally {
      setIsLoading(false)
    }
  }, [form, id])

  useEffect(() => {
    loadData()
  }, [loadData])

  const setPeriodDuration = useCallback(
    (months: number) => {
      const currentPeriod = form.getFieldValue('period')
      const start = currentPeriod?.[0]?.startOf('month') || dayjs().startOf('month')
      form.setFieldsValue({ period: [start, start.add(months - 1, 'month')] })
    },
    [form],
  )

  const savePayment = useCallback(async () => {
    setPageErrors([])

    try {
      const values = await form.validateFields()
      setIsSaving(true)
      let result: Record<string, any>
      if (id === 'new') {
        const [periodStart, periodEnd] = values.period as [Dayjs, Dayjs]
        if (periodStart.isAfter(periodEnd, 'month')) {
          setPageErrors(['Начало периода не может быть позже окончания'])
          return
        }
        result = await fetchAPI('/api/hr/payments/distribute', {
          method: 'POST',
          body: JSON.stringify({
            period_start: periodStart.startOf('month').format('YYYY-MM-DD'),
            period_end: periodEnd.startOf('month').format('YYYY-MM-DD'),
            actual_payment_date: values.actual_payment_date?.format('YYYY-MM-DD') || null,
            resource_id: values.resource_id,
            payment_plan: values.payment_plan,
            payment_fact: values.payment_fact,
          }),
        })
      } else {
        result = await fetchAPI(`/api/hr/payments/${id}`, {
          method: 'POST',
          body: JSON.stringify({
            payment_date: values.payment_month?.startOf('month').format('YYYY-MM-DD'),
            actual_payment_date: values.actual_payment_date?.format('YYYY-MM-DD') || null,
            resource_id: values.resource_id,
            payment_plan: values.payment_plan,
            payment_fact: values.payment_fact,
          }),
        })
      }
      const errors = getErrorMessages(result)
      if (errors.length) {
        setPageErrors(errors)
        return
      }

      navigate('/hr/payments')
    } catch (error: any) {
      if (!error?.errorFields) {
        setPageErrors([error instanceof Error ? error.message : 'Не удалось сохранить оплату'])
      }
    } finally {
      setIsSaving(false)
    }
  }, [form, id, navigate])

  return (
    <B2BLayout breadcrumbs={breadcrumbs}>
      <MapWrapper>
        <TaskWrapper>
          <MapTitle>{id === 'new' ? 'Добавление оплаты' : 'Редактирование оплаты'}</MapTitle>
        </TaskWrapper>
        <MapContent>
          {pageErrors.length > 0 && (
            <AlertWrapper>
              <Alert
                message="Ошибка при работе с оплатой"
                showIcon
                type="error"
                description={pageErrors.join('. ')}
              />
            </AlertWrapper>
          )}
          {isLoading ? (
            <Skeleton active />
          ) : loadFailed ? (
            <Button onClick={() => navigate('/hr/payments')}>Вернуться к списку оплат</Button>
          ) : (
            <ConfigProvider locale={ruRU}>
              <Form form={form} layout="vertical" requiredMark="optional" style={{ maxWidth: 680 }}>
                {id === 'new' ? (
                  <>
                    <Form.Item
                      label="Быстрый выбор периода"
                      extra="Период рассчитывается от первого выбранного месяца"
                    >
                      <Space wrap>
                        <Button onClick={() => setPeriodDuration(1)}>Месяц</Button>
                        <Button onClick={() => setPeriodDuration(3)}>Квартал</Button>
                        <Button onClick={() => setPeriodDuration(12)}>Год</Button>
                      </Space>
                    </Form.Item>

                    <Form.Item
                      name="period"
                      label="Период, за который учитывается оплата"
                      rules={[{ required: true, message: 'Выберите начало и окончание периода' }]}
                    >
                      <DatePicker.RangePicker
                        picker="month"
                        format="MM.YYYY"
                        allowClear={false}
                        style={{ width: '100%' }}
                        placeholder={['Начало периода', 'Окончание периода']}
                      />
                    </Form.Item>
                  </>
                ) : (
                  <Form.Item
                    name="payment_month"
                    label="Месяц учёта"
                    rules={[{ required: true, message: 'Выберите месяц учёта' }]}
                  >
                    <DatePicker picker="month" format="MM.YYYY" style={{ width: '100%' }} />
                  </Form.Item>
                )}

                <Form.Item
                  name="actual_payment_date"
                  label="Фактическая дата оплаты"
                  extra="Можно оставить пустой для плановой оплаты или если точная дата неизвестна"
                >
                  <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item
                  name="resource_id"
                  label="Ресурс"
                  rules={[{ required: true, message: 'Выберите ресурс' }]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    options={resources}
                    placeholder="Выберите ресурс"
                  />
                </Form.Item>

                <Form.Item
                  name="payment_plan"
                  label={id === 'new' ? 'План оплат за весь период' : 'План оплат за месяц'}
                  rules={[{ required: true, message: 'Укажите план оплат' }]}
                >
                  <InputNumber min={0} precision={2} addonAfter="₽" style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item
                  name="payment_fact"
                  label={id === 'new' ? 'Факт оплат за весь период' : 'Факт оплат за месяц'}
                  rules={[{ required: true, message: 'Укажите факт оплат' }]}
                >
                  <InputNumber min={0} precision={2} addonAfter="₽" style={{ width: '100%' }} />
                </Form.Item>

                {id === 'new' && monthsCount > 0 && (
                  <Alert
                    showIcon
                    type="info"
                    message={`Распределение на ${getMonthsLabel(monthsCount)}`}
                    description={
                      <div>
                        <div>План: {getMonthlyAmountText(paymentPlan, monthsCount)}</div>
                        <div>Факт: {getMonthlyAmountText(paymentFact, monthsCount)}</div>
                      </div>
                    }
                    style={{ marginBottom: 24 }}
                  />
                )}

                <Space>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    loading={isSaving}
                    onClick={savePayment}
                  >
                    Сохранить
                  </Button>
                  <Button onClick={() => navigate('/hr/payments')}>Отмена</Button>
                </Space>
              </Form>
            </ConfigProvider>
          )}
        </MapContent>
      </MapWrapper>
    </B2BLayout>
  )
}
